import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FORM_ENDPOINT = process.env.REACT_APP_FORM_ENDPOINT || `${BACKEND_URL}/api/contact`;

const servicos = [
  "Direito Penal",
  "Direito Previdenciário",
  "Direito Civil",
  "Direito do Consumidor",
  "Direito Bancário",
  "Direito Médico",
  "Gestão de Afastados e NR-1",
  "Consultoria preventiva",
  "Outros",
];

const initial = { nome: "", email: "", telefone: "", servico: "", mensagem: "" };

export default function Contato() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.telefone || !form.servico || !form.mensagem) {
      toast.error("Por favor, preencha todos os campos.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(FORM_ENDPOINT, form);
      if (data?.success) {
        toast.success("Mensagem enviada com sucesso!", {
          description: "Em breve um de nossos especialistas entrará em contato.",
        });
        setForm(initial);
      } else {
        toast.error("Não foi possível enviar sua mensagem.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Falha no envio", {
        description: typeof detail === "string" ? detail : "Tente novamente em instantes.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" className="bg-brand-primary text-white relative overflow-hidden" data-testid="section-contato">
      <span aria-hidden className="absolute -bottom-20 -left-20 font-serif text-[360px] leading-none text-white/[0.03] select-none">
        G
      </span>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5">
          <p className="eyebrow text-brand-accent mb-6"><span className="thin-rule" />Contato</p>
          <h2 className="font-serif text-white font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Precisa de orientação jurídica <em className="not-italic text-brand-accent">estratégica e segura</em>?
          </h2>
          <p className="mt-6 text-[15px] leading-relaxed text-white/75 max-w-md">
            Conte com uma advocacia preparada para proteger seus interesses com técnica, experiência
            e comprometimento.
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75 max-w-md">
            Preencha o formulário abaixo para entrar em contato com um dos nossos especialistas.
          </p>

          <div className="mt-10 space-y-4 text-sm text-white/80">
            <p><span className="text-brand-accent eyebrow block mb-1">Endereço</span>Rua Santa Luzia, 48 — conj. 38 · Liberdade · São Paulo / SP</p>
            <p><span className="text-brand-accent eyebrow block mb-1">Telefone</span><a href="tel:+551131017576" className="hover:text-brand-accent">(11) 3101-7576</a></p>
            <p><span className="text-brand-accent eyebrow block mb-1">WhatsApp</span><a href="https://wa.me/5511976658938" target="_blank" rel="noreferrer" className="hover:text-brand-accent">(11) 97665-8938</a></p>
            <p><span className="text-brand-accent eyebrow block mb-1">E-mail</span><a href="mailto:contato@galleti.com.br" className="hover:text-brand-accent">contato@galleti.com.br</a></p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-7 bg-white/[0.03] border border-white/10 p-8 lg:p-10 space-y-2"
          data-testid="contact-form"
          noValidate
        >
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
            <div>
              <label className="eyebrow text-brand-accent block mb-2">Nome completo</label>
              <input
                value={form.nome}
                onChange={onChange("nome")}
                placeholder="Como podemos te chamar"
                className="dark-input"
                data-testid="contact-input-nome"
                required
              />
            </div>
            <div>
              <label className="eyebrow text-brand-accent block mb-2">E-mail</label>
              <input
                type="email"
                value={form.email}
                onChange={onChange("email")}
                placeholder="seu@email.com"
                className="dark-input"
                data-testid="contact-input-email"
                required
              />
            </div>
            <div>
              <label className="eyebrow text-brand-accent block mb-2">Telefone</label>
              <input
                value={form.telefone}
                onChange={onChange("telefone")}
                placeholder="(11) 9 0000-0000"
                className="dark-input"
                data-testid="contact-input-telefone"
                required
              />
            </div>
            <div>
              <label className="eyebrow text-brand-accent block mb-2">Serviço de interesse</label>
              <Select value={form.servico} onValueChange={onChange("servico")}>
                <SelectTrigger
                  className="bg-transparent border-0 border-b border-white/25 rounded-none px-0 py-3 h-auto text-white hover:border-brand-accent focus:ring-0 focus:border-brand-accent data-[placeholder]:text-white/40 [&>span]:text-[15px]"
                  data-testid="contact-select-servico"
                >
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent className="bg-brand-primary border-white/10 text-white">
                  {servicos.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      className="text-white focus:bg-brand-accent focus:text-brand-primary"
                      data-testid={`contact-select-option-${s.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="pt-2">
            <label className="eyebrow text-brand-accent block mb-2">Mensagem</label>
            <textarea
              value={form.mensagem}
              onChange={onChange("mensagem")}
              placeholder="Conte brevemente sobre sua demanda"
              rows={4}
              className="dark-input resize-none"
              data-testid="contact-input-mensagem"
              required
            />
          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-[11px] tracking-wider text-white/40 max-w-md">
              Ao enviar você concorda em ser contatado por nossa equipe sobre o atendimento.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit"
              className="group inline-flex items-center gap-3 bg-brand-accent text-brand-primary px-8 py-4 text-[12px] tracking-[0.25em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Enviando…
                </>
              ) : (
                <>
                  Entrar em contato agora
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
